import styled from "styled-components";
import {
    space,
    layout,
    flexbox,
    grid,
    background,
    color,
    position,
    typography,
    border,
    width,
} from "styled-system";
import { ThemeObject } from "../ThemeObjects";

function handleWrapBackgroundImage(imagePath: string | null) {
    if (imagePath) {
        imagePath =
            imagePath.indexOf("url(") === 0 ||
            imagePath.indexOf("gradient") !== -1
                ? imagePath
                : `url(${imagePath})`;

        return imagePath;
    }
    return "";
}

/**
 * True when the element carries an `imported-design-*` scope class in its
 * `tailwindClasses` prop. Inline color and backgroundColor are dropped in
 * that case so the scoped imported CSS — which the parser hoists from
 * `body { background: ... }` to the scope wrapper — can paint the container.
 * Without this, inline styles would always win over scoped class rules and
 * the design would render with SimBuilder's defaults.
 *
 * Callers must pass `tailwindClasses` explicitly as a prop (in addition to
 * the className interpolation) for this to fire. PageTheme does this; other
 * themes short-circuit to a plain div before reaching StyledUniversalDiv.
 */
function hasImportedDesignScope(classes: unknown): boolean {
    if (!Array.isArray(classes)) return false;
    return classes.some(
        (c) => typeof c === "string" && c.startsWith("imported-design-"),
    );
}

export const StyledUniversalDiv = styled.div.attrs(
    (props: ThemeObject & { dataContentBlockId?: string }) => {
        const importMode = hasImportedDesignScope(
            (props as { tailwindClasses?: unknown }).tailwindClasses,
        );

        const style = {
            color: importMode ? undefined : props.color,
            backgroundColor: importMode ? undefined : props.backgroundColor,
            opacity: props.opacity,
            backgroundImage: importMode
                ? undefined
                : handleWrapBackgroundImage(
                      props.backgroundImage as unknown as string,
                  ),
        };

        return {
            style,
        };
    }
)<ThemeObject>`
    ${width}
    ${space}
    ${layout}
    ${flexbox}
    ${grid}
    ${background}
    /* color */
    ${position}
    ${typography}
    ${border}
    // additional
    text-transform: ${(props) => props.textTransform || "none"};
    cursor: ${(props) => props.cursor || "auto"};
`;
StyledUniversalDiv.displayName = "StyledUniversalDiv";

//use role={"button"}
export const StyledUniversalButton = styled.button<ThemeObject>`
    ${space}
    ${layout}
    ${flexbox}
    ${grid}
    ${background}
    ${color}
    ${position}
    ${typography}
    ${border}
    // additional
    text-transform: ${(props) => props.textTransform || "none"};
    cursor: ${(props) => props.cursor || "auto"};

`;
StyledUniversalButton.displayName = "StyledUniversalButton";

export const StyledUniversalLinkDiv = styled.div<ThemeObject>`
    ${space}
    ${layout}
    ${flexbox}
    ${grid}
    ${background}
    /* color */
    ${position}
    ${typography}
    ${border}
    // additional
    text-transform: ${(props) => props.textTransform || "none"};
    cursor: ${(props) => props.cursor || "auto"};
    background-color: none;
    // child link/span styling
    > button,
    > div,
    > a,
    span {
        color: ${(props) => props.color};
        cursor: ${(props) => props.cursor || "auto"};
        opacity: ${(props) => props.opacity};
        transition: 0.3s;
        text-decoration: none;
        &:hover,
        &.active {
            opacity: 1;
            text-decoration: none;
            color: ${(props) =>
                props.activeColor ? props.activeColor : props.color};
        }
        display: ${(props) => props.display};
        align-items: ${(props) => props.alignItems};
    }
`;
StyledUniversalLinkDiv.displayName = "StyledUniversalLinkDiv";

export const SytledUniversalImage = styled.img<ThemeObject>`
    ${border}
    ${layout}
`;

SytledUniversalImage.displayName = "SytledUniversalImage";

export const StyledUniversalSection = styled.section.attrs(
    (props: ThemeObject) => {
        // console.log("PROPS", props);
        return {
            style: {
                color: props.color,
                backgroundColor: props.backgroundColor,
                opacity: props.opacity,
                backgroundImage: props.backgroundImage,
            },
        };
    }
)<ThemeObject>`
    ${space}
    ${layout}
    ${flexbox}
    ${grid}
    ${background}
    /* color */
    ${position}
    ${typography}
    ${border};
    // additional
    text-transform: ${(props) => props.textTransform || "none"};
    cursor: ${(props) => props.cursor || "auto"};
    background-image: ${({ backgroundImage }) =>
        handleWrapBackgroundImage(backgroundImage as string)};
    background-size: cover;
    background-position ${({ backgroundPosition }) =>
        backgroundPosition || "center"};
`;
StyledUniversalSection.displayName = "StyledUniversalSection";
